import React from 'react';
import PropTypes from 'prop-types';
import oneLine from 'common-tags/lib/oneLine';
import htmlescape from 'htmlescape';

import { withRouter } from 'core/libs/router';
import { get } from 'core/libs/lodash';
import { Helmet } from 'core/libs/helmet';

import resolveRelationships from 'core/utils/relationships';
import removeMarkdown from 'remove-markdown';
import { getFullLink, isPreviewPath } from 'core/utils/url-helper';
import { formatToMoscowTimeZone } from 'core/utils/dates';
import { getMetaCover } from 'core/utils/smart-image';
import { getDescription } from 'core/utils/content';

const aspectRatios = [[1280, 720], [1280, 960], [1280, 1280]];

/**
 * Базовое отображение топика.
 * Формирует метатеги для топика и вызывает функцию для отрисовки.
 *
 * @author Anton Ignatov <a.ignatov@rambler-co.ru>
 */
function TopicsMeta(props, context) {
  const {
    titleBuilder,
    descriptionBuilder,
    hideAmpLink,
    content: {
      attributes: {
        headline,
        alternative_headline: alternativeHeadline,
        announce,
        published_at: publishedAt,
        updated_at: updatedAt,
        link,
        topic_type: topicType,
        has_adult_content: hasAdultContent,
      },
    },
    location: {
      pathname,
    },
  } = props;

  const {
    amp: isAmpPage,
    host,
    webdavHost,
    thumborHost,
    isUnityProject,
    schemaOrg: {
      organizationName,
      organizationLogoUrl,
      organizationLogoWidth,
      organizationLogoHeight,
    },
    ampConfig: {
      startAmpDate,
    },
    features: {
      useSmartResizeForSocialImage,
      disableAmp,
    },
    mediaUrlPrefix,
  } = context;

  const {
    image: {
      versions: {
        original: originalCover,
        social_network: ogCover,
        social_vk: vkCover,
      },
    },
    content: {
      widgets,
    },
    authors,
    rubric,
    rubric_root: rubricRoot,
    seo_meta: {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
      noindex: seoNoIndex,
    },
    video,
  } = resolveRelationships(...[
    ['image', 'content', 'rubric', 'seo_meta', 'imported_topic', 'authors', 'rubric_root', 'video'],
    {},
    {
      image: {
        versions: {
          original: {},
          social_network: {},
          social_vk: {},
        },
      },
      content: { widgets: [] },
      seo_meta: {},
      imported_topic: {},
      authors: [],
      video: {},
    },
  ])(props.content);

  const videoMicrodata = video.microdata;

  const title = typeof titleBuilder === 'function'
    ? titleBuilder(props.content)
    : seoTitle || headline;
  let description = typeof descriptionBuilder === 'function'
    ? descriptionBuilder(props.content)
    : seoDescription || announce || getDescription(widgets);
  description = removeMarkdown(description);

  const rubricsTitle = rubric.title === rubricRoot.title ? rubric.title : `${rubricRoot.title}: ${rubric.title}`;
  const fullSeoTitle = isUnityProject ? `${title}: ${rubricsTitle}` : title;

  const hostName = host ? `https://${host}` : '';

  const canonical = `${hostName}${link}`;
  const ampLink = `${hostName}/amp${link}`;

  const isModernTopic = !startAmpDate || new Date(publishedAt).getTime() > new Date(startAmpDate).getTime();
  const ampDisabled = disableAmp || hideAmpLink;
  const showAmpLink = isModernTopic && !isAmpPage && !ampDisabled;

  // Формируем микроразметку статьи
  const author = get(authors, '[0].attributes');

  const schemaAuthorType = !author || author.is_organization ? 'Organization' : 'Person';
  const schemaAuthorName = author && author.name ? author.name : organizationName;
  const schemaAuthorUrl = author && author.slug && `${hostName}${mediaUrlPrefix || ''}/authors/${author.slug}`;

  const jsonLd = {
    '@context': 'http://schema.org',
    'datePublished': formatToMoscowTimeZone(publishedAt),
    'dateModified': formatToMoscowTimeZone(updatedAt),
    'description': announce || getDescription(widgets),
    'alternativeHeadline': alternativeHeadline || headline,
    'author': {
      '@type': schemaAuthorType,
      'name': schemaAuthorName,
      ...(schemaAuthorUrl && { 'url': schemaAuthorUrl }),
    },
    'publisher': {
      '@type': 'Organization',
      'name': organizationName,
      'logo': {
        '@type': 'ImageObject',
        'url': organizationLogoUrl,
        'width': organizationLogoWidth,
        'height': organizationLogoHeight,
      },
    },
  };

  if (topicType === 'video' && videoMicrodata) {
    jsonLd['@type'] = 'VideoObject';
    jsonLd.name = videoMicrodata.name;
    jsonLd.url = videoMicrodata.content_url;
    jsonLd.thumbnailUrl = videoMicrodata.thumbnail_url;
    jsonLd.embedUrl = videoMicrodata.embed_url;
    jsonLd.genre = videoMicrodata.genre;
    jsonLd.duration = videoMicrodata.duration;
    jsonLd.uploadDate = formatToMoscowTimeZone(videoMicrodata.upload_date);

    if (videoMicrodata.description) {
      jsonLd.description = videoMicrodata.description;
    }

    if (videoMicrodata.is_family_friendly !== null) {
      jsonLd.isFamilyFriendly = videoMicrodata.is_family_friendly;
    }

    if (videoMicrodata.date_modified) {
      jsonLd.dateModified = formatToMoscowTimeZone(videoMicrodata.date_modified);
    }
  } else {
    jsonLd['@type'] = topicType === 'news' ? 'NewsArticle' : 'Article';
    jsonLd.headline = headline;
    jsonLd.url = canonical;
    jsonLd.articleBody =  getArticleBody(widgets, announce);
    jsonLd.mainEntityOfPage = {
      '@type': 'WebPage',
      '@id': canonical,
    };

    // Формируем images для schema.org
    const metaCovers = aspectRatios.reduce((acc, [width, height]) => {
      const cover = getMetaCover({
        secondary: originalCover,
        webdavHost,
        thumborHost,
        smart: true,
        width,
        height,
      });

      return cover ? [...acc, cover] : acc;
    }, []);

    const imagesMeta = metaCovers.map(cover => {
      const fullLink = getFullLink(cover.rel_url, hostName);
      return {
        '@type': 'ImageObject',
        'representativeOfPage': 'true',
        'url': fullLink,
        'width': cover.width,
        'height': cover.height,
      };
    });

    const defaultOgImageParameters = {
      'url': `https://${host}/icons/og-image.png`,
      'width': 1280,
      'height': 720,
    };

    jsonLd.image = imagesMeta.length ? imagesMeta : {
      '@type': 'ImageObject',
      'representativeOfPage': 'true',
      defaultOgImageParameters,
    };
  }

  const jsonBreadcrumbListLd = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Главная страница',
        'item': hostName,
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': rubric.root_title,
        'item': `${hostName}/${rubric.root_slug}`,
      },
    ],
  };

  if (rubric.slug !== rubric.root_slug) {
    jsonBreadcrumbListLd.itemListElement.push({
      '@type': 'ListItem',
      'position': 3,
      'name': rubric.title,
      'item': `${hostName}/${rubric.root_slug}/${rubric.slug}`,
    });
  }

  jsonBreadcrumbListLd.itemListElement.push({
    '@type': 'ListItem',
    'position': jsonBreadcrumbListLd.itemListElement.length + 1,
    'name': headline,
  });

  // Формируем og image
  const metaCoverWebdav = getMetaCover({
    primary: ogCover,
    secondary: originalCover,
    webdavHost,
    thumborHost,
    smart: useSmartResizeForSocialImage,
  });

  const ogImage = metaCoverWebdav ? [
    <meta key='og:image'
      property='og:image'
      content={getFullLink(metaCoverWebdav.rel_url, hostName)}
    />,
    <meta key='og:image:width'
      property='og:image:width'
      content={metaCoverWebdav.width}
    />,
    <meta key='og:image:height'
      property='og:image:height'
      content={metaCoverWebdav.height}
    />,
  ] : null;

  // Для вк не надо делать фолбек, выводится только если указан.
  const vkCoverWebdav = getMetaCover({ primary: vkCover });
  const isPreview = isPreviewPath(pathname);

  const metaRobotsContent = seoNoIndex || isPreview
    ? 'noindex'
    : 'max-image-preview:large, max-video-preview:5';

  return (
    <Helmet>
      <title>{fullSeoTitle}</title>
      <meta name='description' content={description} />
      {seoKeywords && <meta name='keywords' content={seoKeywords} />}
      <meta property='og:title' content={title} />
      <meta property='og:description' content={description} />
      <meta property='og:type' content='article' />
      <meta property='og:url' content={canonical} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='robots' content={metaRobotsContent} />
      {hasAdultContent && <meta name='rating' content='adult' />}
      {ogImage}
      <link rel='canonical' href={canonical} />
      {showAmpLink && <link rel='amphtml' href={ampLink} />}
      {vkCoverWebdav && vkCoverWebdav.size && (
        <meta property='vk:image' content={getFullLink(vkCoverWebdav.rel_url, hostName)} />
      )}
      <script type='application/ld+json'>{htmlescape(jsonLd)}</script>
      <script type='application/ld+json'>{htmlescape(jsonBreadcrumbListLd)}</script>
    </Helmet>
  );
}

TopicsMeta.propTypes = {
  /**
   * Данные топика со всеми зависимостями `include=all`
   */
  content: PropTypes.object.isRequired,
  /**
   * Функция для генерации тайтла станицы.
   * Используется для сложных SEO шаблонов, зависящих от рубрики, типа топика, пагинации и прочего.
   */
  titleBuilder: PropTypes.func,
  /**
   * Функция для генерации описания страницы.
   * Используется для сложных SEO шаблонов, зависящих от рубрики, типа топика, пагинации и прочего.
   */
  descriptionBuilder: PropTypes.func,
  /**
   * Для скрытия амп-ссылки в мета-тегах `link[rel='amphtml']`.
   *
   * Например:
   * - для топиков в которых нет AMP версий `/stars/star_slug`
   *
   * Если требуется скрыть AMP глобально, следует использовать `features.disableAmp`
   */
  hideAmpLink: PropTypes.bool,
  location: PropTypes.object,
};

TopicsMeta.contextTypes = {
  host: PropTypes.string,
  webdavHost: PropTypes.string,
  thumborHost: PropTypes.string,
  amp: PropTypes.bool,
  isUnityProject: PropTypes.bool,
  schemaOrg: PropTypes.shape({
    organizationName: PropTypes.string.isRequired,
    organizationLogoUrl: PropTypes.string.isRequired,
    organizationLogoWidth: PropTypes.number.isRequired,
    organizationLogoHeight: PropTypes.number.isRequired,
  }),
  ampConfig: PropTypes.shape({
    startAmpDate: PropTypes.string,
  }),
  features: PropTypes.shape({
    useSmartResizeForSocialImage: PropTypes.bool,
    disableAmp: PropTypes.bool,
  }),
  mediaUrlPrefix: PropTypes.string,
};

export { TopicsMeta as StorybookComponent };
export default withRouter(TopicsMeta);

function getArticleBody(widgets, announce) {
  return widgets.reduce((result, widget) => {
    if (widget.type === 'markdown') {
      return result + oneLine`${removeMarkdown(widget.attributes.body)}`;
    }
    return result;
  }, announce ? announce + ' ' : '');
}
